// Reusable document slots for a client (customer or company). Stores files in
// the `client-docs` bucket and one current file per (entity, file_type) in
// `client_documents`. Drop into any edit page: <ClientDocuments entityType="customer" entityId={id} />.
// Whatever leaves a slot (replaced or removed) is archived by a DB trigger into
// `client_other_documents`, shown in the "Otros documentos" card with the
// files that don't fit a slot.
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  Button,
  Spinner,
  Badge,
} from "@shopify/polaris";
import supabase from "../../../supabase-client";
import { UserAuth } from "../../../context/AuthContext";
import { formatFriendlyDate } from "../../../utility/funcs";

export const CUSTOMER_DOC_SLOTS = [
  { type: "ine_front", label: "INE Frente" },
  { type: "ine_back", label: "INE Reverso" },
  { type: "csf", label: "Constancia de Situación Fiscal" },
  { type: "proof_address", label: "Comprobante de domicilio" },
  { type: "license_front", label: "Licencia Frente" },
  { type: "license_back", label: "Licencia Reverso" },
];
export const COMPANY_DOC_SLOTS = [
  { type: "acta_constitutiva", label: "Acta Constitutiva" },
  { type: "csf", label: "Constancia de Situación Fiscal" },
  { type: "proof_address", label: "Comprobante de domicilio" },
  { type: "ine_rep_front", label: "INE Rep. Legal Frente" },
  { type: "ine_rep_back", label: "INE Rep. Legal Reverso" },
];
const BUCKET = "client-docs";

function ClientDocuments({ entityType, entityId, title = "Documentos" }) {
  const { session } = UserAuth();
  const slots = entityType === "company" ? COMPANY_DOC_SLOTS : CUSTOMER_DOC_SLOTS;
  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [others, setOthers] = useState([]);
  const [busyType, setBusyType] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);
  const pendingType = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("client_documents")
      .select("*")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId);
    const { data: otherRows } = await supabase
      .from("client_other_documents")
      .select("*")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .order("created_at", { ascending: false });
    const sign = async (row) => {
      const { data: signed } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(row.file_url, 3600);
      return { row, url: signed?.signedUrl || null };
    };
    const map = {};
    for (const d of await Promise.all((data || []).map(sign))) map[d.row.file_type] = d;
    setDocs(map);
    setOthers(await Promise.all((otherRows || []).map(sign)));
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = (type) => {
    pendingType.current = type;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    const type = pendingType.current;
    e.target.value = "";
    if (!file || !type || !entityId) return;
    setBusyType(type);
    try {
      const safe = file.name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${entityType}/${entityId}/${type}_${Date.now()}_${safe}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      if (type === "other") {
        const { error: dbErr } = await supabase.from("client_other_documents").insert({
          entity_type: entityType,
          entity_id: entityId,
          file_url: path,
          file_name: file.name,
          created_by: session?.user?.id || null,
        });
        if (dbErr) throw dbErr;
        await load();
        return;
      }
      // Replacing: the trigger moves the current file to Otros documentos.
      const { error: dbErr } = await supabase.from("client_documents").upsert(
        {
          entity_type: entityType,
          entity_id: entityId,
          file_type: type,
          file_url: path,
          file_name: file.name,
          created_by: session?.user?.id || null,
        },
        { onConflict: "entity_type,entity_id,file_type" }
      );
      if (dbErr) throw dbErr;
      await load();
    } catch (err) {
      alert("Error al subir el documento: " + (err.message || err));
    } finally {
      setBusyType(null);
    }
  };

  const run = async (key, fn) => {
    setBusyType(key);
    try {
      await fn();
      await load();
    } catch (err) {
      alert("Error: " + (err.message || err));
    } finally {
      setBusyType(null);
    }
  };

  // Takes the file out of its slot; the trigger keeps it in Otros documentos.
  const removeDoc = (type) =>
    run(type, async () => {
      const { error } = await supabase.from("client_documents").delete().eq("id", docs[type].row.id);
      if (error) throw error;
    });

  // Back into its slot; whatever is there now goes to Otros (trigger).
  const restoreOther = (d) =>
    run(`other:${d.row.id}`, async () => {
      const { error } = await supabase.from("client_documents").upsert(
        {
          entity_type: entityType,
          entity_id: entityId,
          file_type: d.row.original_file_type,
          file_url: d.row.file_url,
          file_name: d.row.file_name,
          created_by: session?.user?.id || null,
        },
        { onConflict: "entity_type,entity_id,file_type" }
      );
      if (error) throw error;
      await supabase.from("client_other_documents").delete().eq("id", d.row.id);
    });

  // The only place a client's file is deleted for good.
  const deleteOther = (d) =>
    run(`other:${d.row.id}`, async () => {
      const { error } = await supabase.from("client_other_documents").delete().eq("id", d.row.id);
      if (error) throw error;
      await supabase.storage.from(BUCKET).remove([d.row.file_url]);
      setConfirmDelete(null);
    });

  if (!entityId) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd">{title}</Text>
          <Text tone="subdued">Guarda el cliente primero para adjuntar documentos.</Text>
        </BlockStack>
      </Card>
    );
  }

  const slotLabel = (type) => slots.find((x) => x.type === type)?.label || type;

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd">{title}</Text>
          {loading ? (
            <Spinner size="small" />
          ) : (
            <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
              {slots.map((slot) => {
                const d = docs[slot.type];
                const name = d?.row?.file_name || d?.row?.file_url || "";
                const isImg = d?.url && /\.(jpe?g|png|webp|gif)$/i.test(name);
                return (
                  <BlockStack key={slot.type} gap="100">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="bodySm" fontWeight="semibold">{slot.label}</Text>
                      {d ? (
                        <Badge tone="success" size="small">Cargado</Badge>
                      ) : (
                        <Badge size="small">Falta</Badge>
                      )}
                    </InlineStack>
                    <div
                      style={{
                        border: "1px solid var(--p-color-border)",
                        borderRadius: 8,
                        overflow: "hidden",
                        height: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "var(--p-color-bg-surface-secondary)",
                        cursor: d?.url ? "pointer" : "default",
                      }}
                      onClick={() => d?.url && window.open(d.url, "_blank")}
                    >
                      {d?.url ? (
                        isImg ? (
                          <img
                            src={d.url}
                            alt={slot.label}
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <Text variant="bodySm">Ver documento</Text>
                        )
                      ) : (
                        <Text tone="subdued" variant="bodySm">Sin archivo</Text>
                      )}
                    </div>
                    <InlineStack gap="150">
                      <Button size="slim" onClick={() => onPick(slot.type)} loading={busyType === slot.type}>
                        {d ? "Reemplazar" : "Subir"}
                      </Button>
                      {d && (
                        <Button
                          size="slim"
                          tone="critical"
                          variant="plain"
                          onClick={() => removeDoc(slot.type)}
                          disabled={busyType === slot.type}
                        >
                          Quitar
                        </Button>
                      )}
                    </InlineStack>
                  </BlockStack>
                );
              })}
            </InlineGrid>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            onChange={onFile}
          />
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Otros documentos</Text>
            <Button size="slim" onClick={() => onPick("other")} loading={busyType === "other"}>
              Subir
            </Button>
          </InlineStack>
          {loading ? (
            <Spinner size="small" />
          ) : others.length === 0 ? (
            <Text tone="subdued">Sin otros documentos.</Text>
          ) : (
            <BlockStack gap="200">
              {others.map((d) => {
                const key = `other:${d.row.id}`;
                const name = d.row.label || d.row.file_name || d.row.file_url.split("/").pop();
                const canRestore = d.row.original_file_type && slots.some((x) => x.type === d.row.original_file_type);
                return (
                  <InlineStack key={d.row.id} align="space-between" blockAlign="center" gap="200" wrap={false}>
                    <BlockStack gap="050">
                      <Button variant="plain" onClick={() => d.url && window.open(d.url, "_blank")} disabled={!d.url}>
                        {name}
                      </Button>
                      <Text tone="subdued" variant="bodySm">
                        {d.row.original_file_type
                          ? `Antes en ${slotLabel(d.row.original_file_type)}${
                              d.row.replaced_at ? ` · ${formatFriendlyDate(d.row.replaced_at)}` : ""
                            }`
                          : formatFriendlyDate(d.row.created_at)}
                      </Text>
                    </BlockStack>
                    <InlineStack gap="150" wrap={false}>
                      {canRestore && (
                        <Button size="slim" onClick={() => restoreOther(d)} loading={busyType === key}>
                          Restaurar
                        </Button>
                      )}
                      {confirmDelete === d.row.id ? (
                        <Button size="slim" tone="critical" onClick={() => deleteOther(d)} loading={busyType === key}>
                          ¿Eliminar definitivamente?
                        </Button>
                      ) : (
                        <Button
                          size="slim"
                          tone="critical"
                          variant="plain"
                          onClick={() => setConfirmDelete(d.row.id)}
                          disabled={busyType === key}
                        >
                          Eliminar
                        </Button>
                      )}
                    </InlineStack>
                  </InlineStack>
                );
              })}
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

export default ClientDocuments;
